import styled, { css } from "styled-components";

// Two states. At rest only a small light runs round the edge of the pill, one
// lap per ORBIT_MS. With the pointer on the pill the full light (glow around
// it, light inside, bright edge) stands at the pointer and follows it, with
// the same strength and size anywhere on the pill (SeeInAction.js).
export const ORBIT_MS = 4000;

// The path of the small light: along the edge of a pill w x h (a straight
// top and bottom, half circles at the ends), clockwise from the top left.
// Each keyframe sits at its share of the way round, so with linear timing the
// light keeps one speed on the straight parts and on the round ends alike.
// It turns with the edge, so its long side always lies along it.
export const orbitFrames = (w, h) => {
  const inset = 1;
  const r = h / 2 - inset;
  const left = h / 2;
  const right = w - h / 2;
  const cy = h / 2;
  const steps = 24; // points on each round end
  const points = [[left, inset, 0]];
  points.push([right, inset, 0]);
  for (let i = 1; i <= steps; i += 1) {
    const a = (Math.PI * i) / steps;
    points.push([
      right + r * Math.sin(a),
      cy - r * Math.cos(a),
      (180 * i) / steps,
    ]);
  }
  points.push([left, h - inset, 180]);
  for (let i = 1; i <= steps; i += 1) {
    const a = (Math.PI * i) / steps;
    points.push([
      left - r * Math.sin(a),
      cy + r * Math.cos(a),
      180 + (180 * i) / steps,
    ]);
  }
  const way = [0];
  for (let i = 1; i < points.length; i += 1) {
    const [x0, y0] = points[i - 1];
    const [x1, y1] = points[i];
    way.push(way[i - 1] + Math.hypot(x1 - x0, y1 - y0));
  }
  const total = way[way.length - 1];
  return points.map(([x, y, turn], i) => ({
    offset: way[i] / total,
    transform: `translate(${x.toFixed(2)}px, ${y.toFixed(
      2
    )}px) rotate(${turn.toFixed(1)}deg)`,
  }));
};

// The full light hangs in the middle of the pill and moves along it by
// --glow-dx (set on hover). It shows only while the pill is lit. Near the
// ends the glow outside is round; along the middle the pill would hide most
// of it, so there a glow that hugs the top and bottom edges takes over
// (--glow-round, --glow-edge, --glow-edge-w; SeeInAction.js) and the light
// looks as strong anywhere on the pill. When the pointer leaves it goes out
// quickly, before the pointer is far from the pill.
const FOLLOW = "transform 0.55s cubic-bezier(0.22, 0.61, 0.36, 1)";
const light = (w, h) => css`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${w}px;
  height: ${h}px;
  margin: ${-h / 2}px 0 0 ${-w / 2}px;
  pointer-events: none;
  opacity: 0;
  transform: translateX(var(--glow-dx, 0px));
  transition: ${FOLLOW}, opacity 0.18s ease-out;
`;

// The small light at rest: its keyframes (orbitFrames) move its centre along
// the edge; it shows once they are set (.has-orbit).
const dot = (w, h) => css`
  position: absolute;
  top: 0;
  left: 0;
  width: ${w}px;
  height: ${h}px;
  margin: ${-h / 2}px 0 0 ${-w / 2}px;
  pointer-events: none;
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.35s ease;
`;

// The second button of the hero: smaller and darker than the download button,
// so the download stays the one main action; only its light draws the eye.
export const SeeInActionWrap = styled.span`
  position: relative;
  display: inline-flex;
  vertical-align: middle;

  /* warm glow around the pill; it lies under the download button
     (banner.style.js), so at the left end it never tints it */
  .sia_halo {
    ${light(190, 96)}
    background: radial-gradient(
      closest-side,
      rgba(255, 206, 120, 1),
      rgba(253, 160, 50, 0.6) 45%,
      rgba(253, 150, 42, 0)
    );
    filter: blur(12px);
  }

  .sia_halo_edge {
    ${light(200, 0)}
    top: -32px;
    bottom: -32px;
    height: auto;
    margin: 0 0 0 -100px;
    background: radial-gradient(
        90px 26px at 50% 28px,
        rgba(255, 206, 120, 1),
        rgba(253, 160, 50, 0.6) 45%,
        rgba(253, 150, 42, 0)
      ),
      radial-gradient(
        90px 26px at 50% calc(100% - 28px),
        rgba(255, 206, 120, 1),
        rgba(253, 160, 50, 0.6) 45%,
        rgba(253, 150, 42, 0)
      );
    filter: blur(9px);
    /* shorter near the ends, so it does not reach out past them */
    transform: translateX(var(--glow-dx, 0px)) scaleX(var(--glow-edge-w, 1));
  }

  /* the soft glow of the small light, just outside the edge */
  .sia_dot_glow {
    ${dot(44, 18)}
    background: radial-gradient(
      closest-side,
      rgba(255, 214, 140, 0.85),
      rgba(253, 160, 50, 0.35) 55%,
      rgba(253, 150, 42, 0)
    );
    filter: blur(4px);
  }

  a {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 0.6em;
    height: var(--sia-h, 46px);
    padding: 0 var(--sia-px, 22px);
    border-radius: 999px;
    overflow: hidden;
    isolation: isolate;
    border: 1px solid rgba(255, 255, 255, 0.24);
    background: linear-gradient(
      180deg,
      rgba(36, 58, 94, 0.94),
      rgba(13, 27, 50, 0.94)
    );
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: var(--sia-fs, 14px);
    font-weight: 700;
    letter-spacing: 0.05em;
    line-height: 1;
    text-transform: uppercase;
    text-decoration: none;
    white-space: nowrap;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
    }
    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 3px;
    }
  }

  /* the light inside the pill, dim enough for the white label to keep 4.5:1
     wherever it stands */
  .sia_light {
    ${light(170, 96)}
    z-index: -1;
    border-radius: 50%;
    background: radial-gradient(
      closest-side,
      rgba(255, 208, 135, 0.4),
      rgba(253, 179, 42, 0.2) 50%,
      rgba(253, 179, 42, 0)
    );
  }

  /* the lit edge: lights seen only through a thin ring at the border.
     Without mask-composite the ring is not drawn; the small light then
     shows only as its glow outside the edge. */
  .sia_rim {
    position: absolute;
    inset: 0;
    z-index: -1;
    border-radius: inherit;
    padding: 2px;
    pointer-events: none;
    display: none;
    @supports (mask-composite: exclude) {
      display: block;
      mask: linear-gradient(#000 0 0) content-box exclude,
        linear-gradient(#000 0 0);
    }
  }
  .sia_spark {
    ${light(150, 110)}
    border-radius: 50%;
    background: radial-gradient(
      closest-side,
      #fff,
      #ffe2b0 35%,
      rgba(253, 166, 52, 0.75) 60%,
      rgba(253, 150, 42, 0)
    );
    /* taller along the middle, so the top and bottom edges light up too */
    transform: translateX(var(--glow-dx, 0px))
      scaleY(calc(1 + var(--glow-mid, 0)));
  }
  /* the small light on the edge (the ring starts inside the 1px border) */
  .sia_dot {
    ${dot(44, 14)}
    top: -1px;
    left: -1px;
    background: radial-gradient(
      closest-side,
      #fff,
      #ffe2b0 40%,
      rgba(253, 166, 52, 0.6) 70%,
      rgba(253, 150, 42, 0)
    );
  }

  .sia_label {
    position: relative;
  }

  svg {
    position: relative;
    width: 1.25em;
    height: 0.7em;
    flex: none;
    transition: transform 0.3s ease;
  }

  &.has-orbit {
    .sia_dot,
    .sia_dot_glow {
      opacity: 1;
    }
  }

  /* lit: the pointer is on the pill, or it has keyboard focus (the light
     then stands in the middle). The small light steps aside meanwhile. */
  &.is-lit,
  &.is-focus {
    .sia_halo,
    .sia_halo_edge,
    .sia_light,
    .sia_spark {
      transition: ${FOLLOW}, opacity 0.35s ease;
    }
    .sia_halo {
      opacity: var(--glow-round, 1);
    }
    .sia_halo_edge {
      opacity: var(--glow-edge, 0);
    }
    .sia_spark {
      opacity: 1;
    }
    .sia_light {
      opacity: 0.9;
    }
    .sia_dot,
    .sia_dot_glow {
      opacity: 0;
    }
  }
  /* the moment the pointer comes in, the light appears where it is */
  &.is-jump {
    .sia_halo,
    .sia_halo_edge,
    .sia_light,
    .sia_spark {
      transition: opacity 0.35s ease;
    }
  }

  &:hover,
  &:focus-within {
    a {
      border-color: rgba(255, 226, 180, 0.5);
    }
    svg {
      transform: translateX(3px);
    }
  }

  /* reduced motion: the small light stands still and the full light only
     fades in and out in the middle of the pill (SeeInAction.js) */
  @media (prefers-reduced-motion: reduce) {
    .sia_halo,
    .sia_halo_edge,
    .sia_light,
    .sia_spark {
      transition: opacity 0.35s ease;
    }
    a,
    svg {
      transition: none;
    }
    &:hover svg,
    &:focus-within svg {
      transform: none;
    }
  }
`;
